// =====================================================
// useActivityTracker  (Admin Activity Dashboard)
// A single, app-wide hook that transparently captures user activity with zero
// per-page wiring. Mounted once (via <ActivityTracker/>) inside the Router.
//
// What it does, all throttled/debounced so it never slows the app:
//   • Heartbeat on login, on every route change, and every HEARTBEAT_MS.
//   • Tracks "real interaction" (mouse / keyboard / click / scroll / touch) and
//     reports whether any happened since the last beat -> powers idle time.
//   • Ends the session on logout (token disappears) and on tab close/hide
//     (sendBeacon), so online/offline stays accurate.
//   • Pauses beats while the tab is hidden (no wasted requests), resumes on show.
//
// It is completely passive: it reads auth state, never mutates it, and every
// network call is best-effort (failures are swallowed in the API layer).
// =====================================================

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { isAuthenticated } from "../utils/session";
import { getPageName } from "../utils/pageNames";
import {
  sendHeartbeat,
  endSession,
  clearSessionId,
  getSessionId,
} from "../services/activityApi";

// How often to send a keep-alive while the user sits on a page.
const HEARTBEAT_MS = 30 * 1000;
// Minimum gap between interaction-triggered "wake" beats, to avoid flooding.
const INTERACTION_THROTTLE_MS = 15 * 1000;

export default function useActivityTracker() {
  const location = useLocation();

  // Mutable refs so the interval/listeners always see current values without
  // re-subscribing on every render.
  const interactedRef = useRef(false); // interaction since last beat?
  const lastInteractionBeatRef = useRef(0); // throttle interaction beats
  const routeRef = useRef(location.pathname);
  const hadSessionRef = useRef(false); // did we ever open a session?

  routeRef.current = location.pathname;

  // Fire a heartbeat for the current route, tagging whether the user interacted.
  const beat = () => {
    if (!isAuthenticated()) return;
    const route = routeRef.current;
    getSessionId(); // ensure a session id exists (stamps login time once)
    hadSessionRef.current = true;
    sendHeartbeat({
      page: getPageName(route),
      route,
      interacted: interactedRef.current,
    });
    interactedRef.current = false; // reset the window
  };

  // ── ROUTE CHANGES ── beat immediately so page-visit boundaries are precise.
  useEffect(() => {
    if (isAuthenticated()) {
      interactedRef.current = true; // a navigation IS an interaction
      beat();
    } else if (hadSessionRef.current) {
      // Auth vanished (logout / expiry) while we had a live session -> close it.
      endSession();
      clearSessionId();
      hadSessionRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ── INTERVAL + INTERACTION LISTENERS + LIFECYCLE ──
  useEffect(() => {
    // Record any meaningful interaction. Throttled so a stream of mousemoves
    // doesn't spam beats: we only send an early beat if enough time has passed.
    const onInteract = () => {
      interactedRef.current = true;
      const now = Date.now();
      if (now - lastInteractionBeatRef.current > INTERACTION_THROTTLE_MS) {
        lastInteractionBeatRef.current = now;
        beat();
      }
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((e) =>
      window.addEventListener(e, onInteract, { passive: true })
    );

    // Steady keep-alive while the tab is visible.
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") beat();
    }, HEARTBEAT_MS);

    // Tab hide -> flush a final beat; tab show -> beat to re-appear online fast.
    const onVisibility = () => {
      if (document.visibilityState === "visible") beat();
    };
    document.addEventListener("visibilitychange", onVisibility);

    // Tab close / reload / navigate-away -> close the session cleanly.
    const onUnload = () => {
      if (hadSessionRef.current) endSession();
    };
    window.addEventListener("pagehide", onUnload);
    window.addEventListener("beforeunload", onUnload);

    // Kick off the first beat right away.
    beat();

    return () => {
      events.forEach((e) => window.removeEventListener(e, onInteract));
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onUnload);
      window.removeEventListener("beforeunload", onUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
